import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faRotateRight, faWifi } from '@fortawesome/free-solid-svg-icons';

import { PrimaryButton } from '../../components/button/Button';
import useAppStatus from '../../shell/app-status/useAppStatus.js';
import './errorPages.css';

// A wait past this gets a word of reassurance under the loader.
export const STILL_WORKING_MS = 6000;
// Past this, the loader says so and offers a way out instead of spinning on with no word.
export const SLOW_LOAD_MS = 20000;

/** Why a slow load is slow, when the app knows: the browser is offline, or the server is not answering. */
const slowReason = ({ offline, serverUnreachable }) => {
    if (offline) {
        return {
            icon: faWifi,
            title: "You're offline.",
            message: "This page will finish loading when your connection is back. You don't need to do anything.",
        };
    }
    if (serverUnreachable) {
        return {
            icon: faClock,
            title: "Can't reach GeoPlay right now.",
            message: "We're retrying in the background, and this page will carry on when GeoPlay answers. You can also reload it.",
        };
    }
    return {
        icon: faClock,
        title: 'This is taking longer than usual.',
        message: 'GeoPlay is still working on it. Keep waiting, or reload the page to try again.',
    };
};

/**
 * The one loader every page and route shows while it waits: the GeoPlay mark inside a spinning brand ring. A wait that
 * runs on says it is still working, then - past SLOW_LOAD_MS - becomes a card that says why when the app knows
 * (offline, server out of reach) and offers Reload Page.
 */
const LoadingPage = () => {
    const [stage, setStage] = useState('loading');
    const status = useAppStatus();

    useEffect(() => {
        const still = setTimeout(() => setStage('still'), STILL_WORKING_MS);
        const slow = setTimeout(() => setStage('slow'), SLOW_LOAD_MS);
        return () => {
            clearTimeout(still);
            clearTimeout(slow);
        };
    }, []);

    const reason = stage === 'slow' ? slowReason(status) : null;

    return (
        <div className={`loading-page${reason ? ' loading-page--slow' : ''}`} aria-busy="true">
            <div className="loading-mark" aria-hidden="true">
                <span className="loading-mark-halo" />
                <span className="loading-mark-ring" />
                <span className="loading-mark-logo">G</span>
            </div>
            {!reason && (
                <div className="loading-text" role="status">
                    <p className="loading-label">
                        Loading
                        <span className="loading-dots" aria-hidden="true">
                            <span className="loading-dot" />
                            <span className="loading-dot" />
                            <span className="loading-dot" />
                        </span>
                    </p>
                    <p className={`loading-hint${stage === 'still' ? ' loading-hint--shown' : ''}`}>
                        Still working on it…
                    </p>
                </div>
            )}
            {reason && (
                <div className="loading-slow" role="status">
                    <span className="loading-slow-badge">
                        <FontAwesomeIcon icon={reason.icon} />
                    </span>
                    <h2 className="loading-slow-title">{reason.title}</h2>
                    <p className="loading-slow-text">{reason.message}</p>
                    {!status.offline && (
                        <PrimaryButton size="small" icon={faRotateRight} onClick={() => window.location.reload()}>
                            Reload Page
                        </PrimaryButton>
                    )}
                </div>
            )}
        </div>
    );
};

export default LoadingPage;
